/**
 * 光带/线性暗光场景（与识别 JSON、手动加光共用枚举，保持短 token）
 * 若增删 id，请同步 `nemo-light-backend/src/services/geminiService.ts` 内 `STRIP_CONTEXT_ENUM_FOR_PROMPT`（后端 rootDir 限制不能直引本文件）。
 */
export const STRIP_CONTEXT_VALUES = [
  'ceiling_cove',
  'floating_ceiling',
  'curtain_pocket',
  'beam_soften',
  'ac_edge',
  'wall_feature',
  'wall_panel',
  'niche',
  'door_frame',
  'corner_guide',
  'under_cabinet',
  'wardrobe',
  'glass_display',
  'shelf_front',
  'drawer',
  'mirror_back',
  'floating_cabinet',
  'stair_nosing',
  'stair_handrail',
  'bed_skirt',
  'skirting',
  'bath_tub',
  'shower_niche',
  'toilet_guide',
  'out_rail',
  'out_seat',
  'building_outline',
  'tree_ring',
  'generic',
] as const;

export type StripContext = (typeof STRIP_CONTEXT_VALUES)[number];

export type StripAxis = 'horizontal' | 'vertical' | 'along_edge';

export function normalizeStripContext(raw: unknown): StripContext {
  const s = String(raw ?? '')
    .trim()
    .toLowerCase()
    .replace(/-/g, '_');
  if ((STRIP_CONTEXT_VALUES as readonly string[]).includes(s)) return s as StripContext;
  return 'generic';
}

export function normalizeStripAxis(raw: unknown): StripAxis | undefined {
  const s = String(raw ?? '')
    .trim()
    .toLowerCase();
  if (s === 'horizontal' || s === 'vertical' || s === 'along_edge') return s;
  return undefined;
}

/** 无用户指定时，按场景推断条带主轴（用于蒙版走向） */
export function defaultAxisForStripContext(ctx: StripContext): StripAxis {
  switch (ctx) {
    case 'curtain_pocket':
    case 'wall_feature':
    case 'corner_guide':
    case 'stair_handrail':
    case 'out_rail':
      return 'vertical';
    case 'mirror_back':
    case 'door_frame':
      return 'along_edge';
    default:
      return 'horizontal';
  }
}

/** 蒙版相对尺寸系数（在短边比例基础上乘） */
export function maskScaleForStripContext(ctx: StripContext): { lenMul: number; thickMul: number } {
  switch (ctx) {
    case 'shelf_front':
    case 'under_cabinet':
    case 'glass_display':
      return { lenMul: 1.25, thickMul: 0.85 };
    case 'skirting':
    case 'floating_cabinet':
    case 'bed_skirt':
      return { lenMul: 1.35, thickMul: 0.75 };
    case 'stair_nosing':
    case 'drawer':
      return { lenMul: 1.1, thickMul: 0.9 };
    case 'curtain_pocket':
    case 'wall_feature':
    case 'niche':
    case 'shower_niche':
      return { lenMul: 0.95, thickMul: 1.05 };
    case 'ceiling_cove':
    case 'floating_ceiling':
    case 'beam_soften':
    case 'ac_edge':
      return { lenMul: 1.15, thickMul: 1.0 };
    case 'generic':
      // 通用光带：蒙版略加厚，给模型「洗墙/洗地」区域而非细线，减少成品白线灯管感
      return { lenMul: 1.18, thickMul: 2.45 };
    default:
      return { lenMul: 1.0, thickMul: 1.0 };
  }
}

/** 中文分组标签（仅 UI） */
export const STRIP_CONTEXT_GROUPS: { title: string; ids: StripContext[] }[] = [
  { title: '顶部', ids: ['ceiling_cove', 'floating_ceiling', 'curtain_pocket', 'beam_soften', 'ac_edge'] },
  { title: '立面', ids: ['wall_feature', 'wall_panel', 'niche', 'door_frame', 'corner_guide'] },
  { title: '柜体家具', ids: ['under_cabinet', 'wardrobe', 'glass_display', 'shelf_front', 'drawer', 'mirror_back'] },
  { title: '地面步进', ids: ['floating_cabinet', 'stair_nosing', 'stair_handrail', 'bed_skirt', 'skirting'] },
  { title: '卫浴', ids: ['bath_tub', 'shower_niche', 'toilet_guide'] },
  { title: '室外景观', ids: ['out_rail', 'out_seat', 'building_outline', 'tree_ring'] },
  { title: '通用', ids: ['generic'] },
];

export const STRIP_CONTEXT_LABEL_ZH: Record<StripContext, string> = {
  ceiling_cove: '回光槽',
  floating_ceiling: '悬浮吊顶缝',
  curtain_pocket: '窗帘盒',
  beam_soften: '梁侧暗光',
  ac_edge: '空调风口边',
  wall_feature: '背景墙洗墙',
  wall_panel: '护墙板/格栅缝',
  niche: '壁龛',
  door_frame: '门/隐形门框',
  corner_guide: '阴阳角导光',
  under_cabinet: '吊柜底',
  wardrobe: '衣柜内',
  glass_display: '玻璃展柜',
  shelf_front: '层板前缘下',
  drawer: '抽屉内',
  mirror_back: '镜后四边',
  floating_cabinet: '悬空柜底',
  stair_nosing: '踏步挑出底',
  stair_handrail: '扶手凹槽下照',
  bed_skirt: '床底',
  skirting: '踢脚线',
  bath_tub: '浴缸底',
  shower_niche: '淋浴壁龛',
  toilet_guide: '马桶后/下',
  out_rail: '栏杆下沿',
  out_seat: '座凳/花池底',
  building_outline: '建筑轮廓',
  tree_ring: '树池边',
  generic: '通用光带',
};
